import React, { useState } from 'react'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Collapse,
  IconButton
} from '@material-ui/core'
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import PurchaseDialog from './PurchaseDialog'

const GRAN_TO_DAYS = { day: 1, week: 7, month: 30, year: 365 }
const lc = (g) => (g ? String(g).toLowerCase() : g)

function waitingDays(card, gran) {
  if (card == null) return 0
  return (card || 0) * (GRAN_TO_DAYS[lc(gran)] || 1)
}

// Map an evaluated benefit (single or compare shape) → policy BenefitDTO.
function normBenefit(b) {
  return {
    type: b.name,
    cover_amount_in_cents: b.cover_amount_in_cents != null ? b.cover_amount_in_cents : 0,
    cover_usage_count: b.cover_quantity != null ? b.cover_quantity : 0,
    benefit_period_cardinality: b.benefit_period_cardinality || 1,
    benefit_period_granuality: lc(b.benefit_period_granularity) || 'year',
    waiting_period_in_days:
      b.waiting_period_days != null
        ? b.waiting_period_days
        : waitingDays(b.waiting_period_cardinality, b.waiting_period_granularity)
  }
}

function normDeps(deps) {
  return (deps || []).map((d) => ({
    relationship: d.relationship,
    first_name: d.first_name || '',
    last_name: d.last_name || '',
    date_of_birth: d.date_of_birth || '',
    premium_cents: d.premium_cents,
    benefits: (d.benefits || []).filter((b) => b.valid !== false).map(normBenefit)
  }))
}

// Normalise either result view into the shape PurchaseDialog consumes.
function singleToPurchaseQuote(results, productName) {
  return {
    product_guid: results.product_guid,
    product_name: productName || results.product_guid,
    package_name: null,
    package_code: null,
    currency: results.currency,
    premium_granularity: lc(results.premium_granularity) || 'month',
    premium_cardinality: results.premium_cardinality || 1,
    lapse_period_days: results.lapse_period_days || 30,
    dependants: normDeps(results.dependants)
  }
}

function compareLineToPurchaseQuote(q) {
  return {
    product_guid: q.product_guid,
    product_name: q.product_name,
    package_name: q.package_name,
    package_code: null,
    currency: q.currency,
    premium_granularity: 'month',
    premium_cardinality: 1,
    lapse_period_days: 30,
    dependants: normDeps(q.dependants)
  }
}

function computeAge(dateOfBirth) {
  if (!dateOfBirth) return 'N/A'
  const dob = new Date(dateOfBirth)
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const m = today.getMonth() - dob.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
    age -= 1
  }
  return age
}

function money(cents, currency) {
  if (cents == null) return 'N/A'
  return (
    (currency ? currency + ' ' : '') +
    (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })
  )
}

function period(card, gran) {
  if (!gran) return null
  return 'per ' + (card && card !== 1 ? card + ' ' : '') + gran.toLowerCase() + (card && card !== 1 ? 's' : '')
}

function waiting(card, gran, days) {
  if (card != null && gran) return card + ' ' + gran.toLowerCase() + (card !== 1 ? 's' : '')
  if (days != null) return days + ' day' + (days !== 1 ? 's' : '')
  return 'None'
}

function coverText(b, currency) {
  if (b.cover_quantity != null) {
    return b.cover_quantity + (b.cover_unit ? ' ' + b.cover_unit : '')
  }
  if (b.cover_amount_in_cents != null) return money(b.cover_amount_in_cents, currency)
  return 'N/A'
}

const SUMMARY_CHIP = {
  fontWeight: 600,
  marginRight: 8,
  marginBottom: 8
}

function SummaryChip({ label, tone }) {
  const tones = {
    neutral: { backgroundColor: '#eef2f4', color: '#173E4B' },
    good: { backgroundColor: '#e6f4ea', color: '#1e4620' },
    warn: { backgroundColor: '#fff4e5', color: '#663c00' }
  }
  return <Chip size="small" label={label} style={{ ...SUMMARY_CHIP, ...(tones[tone] || tones.neutral) }} />
}

function PricingTrace({ trace, currency }) {
  const [open, setOpen] = useState(false)
  if (!trace || trace.length === 0) return null
  return (
    <Box style={{ marginTop: 8, marginBottom: 24 }}>
      <Button
        size="small"
        onClick={() => setOpen(o => !o)}
        startIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        style={{ textTransform: 'none', color: '#173E4B', fontWeight: 600 }}
      >
        Pricing breakdown ({trace.length} node{trace.length !== 1 ? 's' : ''})
      </Button>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box style={{ marginTop: 8 }}>
          {trace.map((node, i) => (
            <Paper
              key={i}
              variant="outlined"
              style={{ padding: 12, marginBottom: 8, backgroundColor: '#fafbfc' }}
            >
              <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {node.context}
                </Typography>
                <Chip size="small" label={node.node_type} style={{ backgroundColor: '#eef2f4', color: '#173E4B' }} />
              </Box>
              <Typography variant="caption" style={{ color: '#666', display: 'block', marginTop: 4 }}>
                base {money(node.base, currency)}
                {(node.tables || []).map((t, ti) => (
                  <span key={ti}>
                    {'  →  '}
                    <strong>{t.table_type}</strong>
                    {t.matched_segments && t.matched_segments.length
                      ? ' [' + t.matched_segments.join(', ') + ']'
                      : ' [no match]'}
                    {t.cell_value != null ? ' (' + t.cell_value + ')' : ''}
                    {' = ' + money(t.running_result, currency)}
                  </span>
                ))}
                {'  ⇒  '}
                <strong style={{ color: '#173E4B' }}>{money(node.result, currency)}</strong>
              </Typography>
            </Paper>
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

function SingleProductResults({ results, productName, onPurchase }) {
  const totalPremium = results.total_premium_cents || 0
  const currency = results.currency || ''
  const dependants = results.dependants || []
  const billing = period(results.premium_cardinality, results.premium_granularity)
  const allocation =
    results.benefit_allocation === 'MixAndMatchByGroup'
      ? 'Mix & Match by Group'
      : results.benefit_allocation === 'SinglePackage'
      ? 'Single Package'
      : results.benefit_allocation

  return (
    <Box>
      <Paper style={{ padding: 16, marginBottom: 24, backgroundColor: '#f5f8f9' }}>
        <Typography variant="h6" style={{ color: '#173E4B' }}>
          {productName || results.product_guid || 'Product'}
        </Typography>
        <Typography
          variant="h5"
          style={{ marginTop: 4, color: '#173E4B', fontWeight: 700 }}
        >
          Total Premium: {money(totalPremium, currency)}
          {billing ? (
            <Typography component="span" variant="body2" style={{ color: '#666', fontWeight: 400 }}>
              {'  ' + billing}
            </Typography>
          ) : null}
        </Typography>

        <Box style={{ marginTop: 12 }}>
          {allocation && <SummaryChip label={'Allocation: ' + allocation} />}
          {results.package_premium_cents != null && (
            <SummaryChip label={'Package premium: ' + money(results.package_premium_cents, currency)} />
          )}
          <SummaryChip label={dependants.length + ' dependant' + (dependants.length !== 1 ? 's' : '')} />
          {results.evaluation_date && (
            <SummaryChip label={'Evaluated: ' + String(results.evaluation_date).slice(0, 10)} />
          )}
          {results.lapse_period_days != null && (
            <SummaryChip label={'Lapse: ' + results.lapse_period_days + ' days'} />
          )}
          <SummaryChip
            label={results.requires_manual_review ? '⚠ Manual review required' : '✓ No manual review'}
            tone={results.requires_manual_review ? 'warn' : 'good'}
          />
        </Box>
        {onPurchase && (
          <Box style={{ marginTop: 12 }}>
            <Button
              variant="contained"
              onClick={onPurchase}
              style={{ backgroundColor: '#fba92c', color: '#173E4B', textTransform: 'none', fontWeight: 600 }}
            >
              Purchase
            </Button>
          </Box>
        )}
      </Paper>

      <PricingTrace trace={results.trace} currency={currency} />

      <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
        Per-Dependant Breakdown
      </Typography>
      <TableContainer component={Paper} style={{ marginBottom: 24 }}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#173E4B' }}>
              <TableCell style={{ color: '#fff' }}>Relationship</TableCell>
              <TableCell style={{ color: '#fff' }}>Name</TableCell>
              <TableCell style={{ color: '#fff' }}>Age</TableCell>
              <TableCell style={{ color: '#fff' }}>Benefits</TableCell>
              <TableCell style={{ color: '#fff' }}>Premium</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dependants.map((dep, idx) => {
              const bens = dep.benefits || []
              const validCount = bens.filter(b => b.valid !== false).length
              return (
                <TableRow key={idx}>
                  <TableCell>{dep.relationship}</TableCell>
                  <TableCell>
                    {[dep.first_name, dep.last_name].filter(Boolean).join(' ') || 'N/A'}
                  </TableCell>
                  <TableCell>{computeAge(dep.date_of_birth)}</TableCell>
                  <TableCell>
                    {validCount}/{bens.length} valid
                  </TableCell>
                  <TableCell>{money(dep.premium_cents, currency)}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
        Benefits Detail
      </Typography>
      {dependants.map((dep, dIdx) => (
        <Box key={dIdx} style={{ marginBottom: 16 }}>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
            {dep.relationship}{' '}
            {[dep.first_name, dep.last_name].filter(Boolean).join(' ')}
            <Typography component="span" variant="caption" style={{ color: '#666', marginLeft: 8 }}>
              {money(dep.premium_cents, currency)} total
            </Typography>
          </Typography>
          {dep.benefits && dep.benefits.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Benefit</TableCell>
                    <TableCell>Cover</TableCell>
                    <TableCell>Premium</TableCell>
                    <TableCell>Waiting</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dep.benefits.map((benefit, bIdx) => (
                    <TableRow key={bIdx}>
                      <TableCell>
                        {benefit.name}
                        {benefit.group_code ? (
                          <Typography variant="caption" style={{ color: '#999', display: 'block' }}>
                            group: {benefit.group_code}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{coverText(benefit, currency)}</TableCell>
                      <TableCell>
                        {money(benefit.premium_cents, currency)}
                        {benefit.premium_granularity ? (
                          <Typography variant="caption" style={{ color: '#999', display: 'block' }}>
                            {period(benefit.premium_cardinality, benefit.premium_granularity)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {waiting(
                          benefit.waiting_period_cardinality,
                          benefit.waiting_period_granularity,
                          benefit.waiting_period_days
                        )}
                      </TableCell>
                      <TableCell>
                        {benefit.valid === false ? (
                          <Chip
                            label={benefit.rejection_reason || 'Rejected'}
                            size="small"
                            style={{ backgroundColor: '#fdecea', color: '#611a15' }}
                          />
                        ) : (
                          <Chip
                            label="Valid"
                            size="small"
                            style={{ backgroundColor: '#e6f4ea', color: '#1e4620' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" style={{ color: '#999' }}>
              No benefits
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  )
}

function ComparisonResults({ results, onPurchase }) {
  const quotes = results.quotes || []
  const [expandedRow, setExpandedRow] = useState(null)

  const toggleRow = idx => {
    setExpandedRow(expandedRow === idx ? null : idx)
  }

  return (
    <Box>
      <Paper style={{ padding: 16, marginBottom: 24, backgroundColor: '#f5f8f9' }}>
        <Typography variant="h6" style={{ color: '#173E4B' }}>
          Comparison Results
        </Typography>
        <Typography variant="body2" style={{ color: '#666' }}>
          {results.quote_count != null ? results.quote_count : quotes.length}{' '}
          quote{quotes.length !== 1 ? 's' : ''} · ranked by premium (lowest first)
        </Typography>
        <Box style={{ marginTop: 12 }}>
          {results.benefit_type && <SummaryChip label={'Benefit: ' + results.benefit_type} />}
          {results.scope && <SummaryChip label={'Scope: ' + results.scope} />}
          {results.evaluation_date && (
            <SummaryChip label={'Evaluated: ' + String(results.evaluation_date).slice(0, 10)} />
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#173E4B' }}>
              <TableCell style={{ color: '#fff' }} />
              <TableCell style={{ color: '#fff' }}>Rank</TableCell>
              <TableCell style={{ color: '#fff' }}>Product</TableCell>
              <TableCell style={{ color: '#fff' }}>Package</TableCell>
              <TableCell style={{ color: '#fff' }}>Package premium</TableCell>
              <TableCell style={{ color: '#fff' }}>Total premium</TableCell>
              <TableCell style={{ color: '#fff' }}>Review</TableCell>
              <TableCell style={{ color: '#fff' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quotes.map((quote, idx) => {
              const best = idx === 0
              return (
                <React.Fragment key={idx}>
                  <TableRow
                    hover
                    onClick={() => toggleRow(idx)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: best ? '#f0fdf4' : undefined
                    }}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedRow === idx ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {idx + 1}
                        {best && (
                          <Chip
                            label="Best price"
                            size="small"
                            style={{ backgroundColor: '#dcfce7', color: '#15803d', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{quote.product_name}</TableCell>
                    <TableCell>{quote.package_name || 'N/A'}</TableCell>
                    <TableCell>{money(quote.package_premium_cents, quote.currency)}</TableCell>
                    <TableCell style={{ fontWeight: best ? 700 : 400, color: best ? '#15803d' : undefined }}>
                      {money(quote.total_premium_cents, quote.currency)}
                    </TableCell>
                    <TableCell>
                      {quote.requires_manual_review ? (
                        <Chip label="Yes" size="small" style={{ backgroundColor: '#fff4e5', color: '#663c00' }} />
                      ) : (
                        <Chip label="No" size="small" style={{ backgroundColor: '#e6f4ea', color: '#1e4620' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {onPurchase && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation()
                            onPurchase(quote)
                          }}
                          style={{ backgroundColor: '#fba92c', color: '#173E4B', textTransform: 'none', fontWeight: 600 }}
                        >
                          Purchase
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingTop: 0, paddingBottom: 0 }} colSpan={8}>
                      <Collapse in={expandedRow === idx} timeout="auto" unmountOnExit>
                        <Box style={{ padding: 16 }}>
                          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                            Dependant &amp; Benefit Breakdown
                          </Typography>
                          {quote.dependants && quote.dependants.length > 0 ? (
                            quote.dependants.map((dep, dIdx) => (
                              <Box key={dIdx} style={{ marginBottom: 16 }}>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                  {dep.relationship}{' '}
                                  {[dep.first_name, dep.last_name].filter(Boolean).join(' ')}
                                  <Typography component="span" variant="caption" style={{ color: '#666', marginLeft: 8 }}>
                                    age {computeAge(dep.date_of_birth)} · {money(dep.premium_cents, quote.currency)} total
                                  </Typography>
                                </Typography>
                                {dep.benefits && dep.benefits.length > 0 ? (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Benefit</TableCell>
                                        <TableCell>Cover</TableCell>
                                        <TableCell>Premium</TableCell>
                                        <TableCell>Status</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {dep.benefits.map((b, bIdx) => (
                                        <TableRow key={bIdx}>
                                          <TableCell>{b.name}</TableCell>
                                          <TableCell>{money(b.cover_amount_in_cents, quote.currency)}</TableCell>
                                          <TableCell>{money(b.premium_cents, quote.currency)}</TableCell>
                                          <TableCell>
                                            {b.valid === false ? (
                                              <Chip
                                                label={b.rejection_reason || 'Rejected'}
                                                size="small"
                                                style={{ backgroundColor: '#fdecea', color: '#611a15' }}
                                              />
                                            ) : (
                                              <Chip
                                                label="Valid"
                                                size="small"
                                                style={{ backgroundColor: '#e6f4ea', color: '#1e4620' }}
                                              />
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography variant="caption" style={{ color: '#999' }}>
                                    No benefits for this dependant
                                  </Typography>
                                )}
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" style={{ color: '#999' }}>
                              No dependant details available
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

function QuotationResults(props) {
  const { results, onClose, productName } = props
  const [purchaseQuote, setPurchaseQuote] = useState(null)

  const isComparison = results.quotes !== undefined

  return (
    <>
      <DialogTitle style={{ backgroundColor: '#173E4B', color: '#fff' }}>
        {isComparison ? 'Quotation Comparison' : 'Product Evaluation'}
        <Typography variant="body2" style={{ marginTop: 4, opacity: 0.8 }}>
          {isComparison
            ? 'Ranked by premium (lowest first)'
            : 'Single product evaluation'}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ marginTop: 16, paddingTop: 16 }}>
        {isComparison ? (
          <ComparisonResults
            results={results}
            onPurchase={(q) => setPurchaseQuote(compareLineToPurchaseQuote(q))}
          />
        ) : (
          <SingleProductResults
            results={results}
            productName={productName}
            onPurchase={() => setPurchaseQuote(singleToPurchaseQuote(results, productName))}
          />
        )}
      </DialogContent>
      <DialogActions
        style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 16 }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          style={{ color: '#173E4B', borderColor: '#173E4B' }}
        >
          Close
        </Button>
      </DialogActions>

      <PurchaseDialog
        open={Boolean(purchaseQuote)}
        quote={purchaseQuote}
        onClose={() => setPurchaseQuote(null)}
      />
    </>
  )
}

export default QuotationResults
